/**
 * View-mode helpers for the profile page: read-only rows built from the edit-profile form
 * fields, and the profile completeness score.
 *
 * Completeness is computed on the client from the same schema-driven fields the edit form
 * shows (plus the resume): the share of those items that have a value. No API is involved.
 */
import { FieldType, type FldListState } from '@/types/application.types';

export type ProfileGroup = 'personal' | 'professional' | 'contact';

export interface ProfileRow {
  key: string;
  label: string;
  value: string;
  group: ProfileGroup;
  /** Already shown in the profile hero (name, role, location, experience) — left out of the detail cards */
  inHero: boolean;
}

const HERO_KEYS = ['firstname', 'lastname', 'currentrole', 'currentlocation'];
const isHeroField = (f: FldListState) => f.subType === 'EXP' || HERO_KEYS.includes((f.key || '').toLowerCase());

/** Which view-mode section a schema field belongs to, by its key */
function groupOf(field: FldListState): ProfileGroup {
  const key = (field.key || '').toLowerCase();
  if (field.isMobileFld || field.isEmailFld || /email|contact|mobile|phone|linkedin/.test(key)) return 'contact';
  if (field.subType === 'EXP' || /role|location|experience|company|skill|notice|salary/.test(key)) return 'professional';
  return 'personal';
}

export interface ProfileCompleteness {
  percent: number;
  /** Titles of the items still empty, in form order */
  missing: string[];
}

const isHidden = (f: FldListState) => f.type === FieldType.EMPTY || f.key === 'experienceInMonths';

function displayOf(field: FldListState): string {
  const option = field.dropDownOptions?.find((o) => o.key === field.value);
  return (option?.displayKey || field.displayValue || field.value || '').trim();
}

/** Value as shown in view mode: experience as "2 years 3 months", mobile numbers with their code */
function valueOf(fields: FldListState[], index: number): string {
  const field = fields[index];
  if (field.subType === 'EXP') {
    return [field.value, fields[index + 1]?.value].filter(Boolean).join(' ');
  }
  const value = displayOf(field);
  if (value && field.isMobileFld && field.countryCode) return `+${field.countryCode.replace(/^\+/, '')} ${value}`;
  return value;
}

export function buildProfileRows(fields: FldListState[]): ProfileRow[] {
  const rows: ProfileRow[] = [];
  fields.forEach((field, index) => {
    if (isHidden(field)) return;
    rows.push({ key: field.key || String(index), label: field.title, value: valueOf(fields, index), group: groupOf(field), inHero: isHeroField(field) });
  });
  return rows;
}

export function computeCompleteness(
  rows: ProfileRow[],
  resume: { title: string; present: boolean },
): ProfileCompleteness {
  const items = [...rows.map((r) => ({ title: r.label, filled: !!r.value })), { title: resume.title, filled: resume.present }];
  const filled = items.filter((i) => i.filled).length;
  return {
    percent: items.length ? Math.round((filled / items.length) * 100) : 0,
    missing: items.filter((i) => !i.filled).map((i) => i.title),
  };
}
